import { Dialog } from '@headlessui/react';
import { X, AlertTriangle, Calendar, User, MapPin, FileText } from 'lucide-react';
import { JobCard } from '../../types/jobcard';
import { useState, useEffect } from 'react';
import { syncATRItems, getATRItems, completeATRItem } from '../../services/jobcardApi';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import ATRItemsBox from '../ATRItemsBox';
import ConfirmATRCompletionModal from './ConfirmATRCompletionModal';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    poJobCard: JobCard;
    targetJobCard: JobCard;
    onConfirm: () => void;
    isLoading?: boolean;
};

export default function ConfirmLinkJobCardModal({ 
    isOpen, 
    onClose, 
    poJobCard, 
    targetJobCard, 
    onConfirm, 
    isLoading = false 
}: Props) {
    const [atrItems, setAtrItems] = useState<any[]>([]);
    const [isLoadingATR, setIsLoadingATR] = useState(false);
    const [isSyncingATR, setIsSyncingATR] = useState(false);
    const [isATRCompletionModalOpen, setIsATRCompletionModalOpen] = useState(false);
    const [selectedATRItem, setSelectedATRItem] = useState<any>(null);

    useEffect(() => {
        if (isOpen && poJobCard.order_number) {
            console.log('ConfirmLinkJobCardModal: Starting ATR sync for PO job card:', poJobCard.id, 'Order:', poJobCard.order_number);
            syncAndFetchATRItems();
        }
    }, [isOpen, poJobCard.id]);

    const syncAndFetchATRItems = async () => {
        try {
            setIsSyncingATR(true);
            console.log('ConfirmLinkJobCardModal: Syncing ATR items for PO job card ID:', poJobCard.id);
            
            // First sync ATR items for the PO job card
            const syncRes = await syncATRItems(poJobCard.id);
            console.log('ConfirmLinkJobCardModal: ATR sync completed, now fetching items');
            
            // Handle different response codes
            const responseData = syncRes?.data;
            if (responseData?.code === 'NO_ASSETS_FOUND') {
                showSuccessToast('No ATR items found for this purchase order.');
                setAtrItems([]);
                return; // Don't fetch items if there are none
            } else if (responseData?.code === 'PO_NOT_FOUND') {
                if (responseData?.deleted_items?.job_card) {
                    // Job card was deleted, show success message and close modal
                    showSuccessToast('Job card deleted as purchase order was not found. Closing modal...');
                    setTimeout(() => {
                        onClose();
                    }, 2000);
                    return; // Don't fetch items if there are none
                } else {
                    showErrorToast(responseData?.message || 'Purchase order not found');
                    setAtrItems([]);
                    return; // Don't fetch items if there are none
                }
            } else if (responseData?.code === 'PO_NOT_INTERNAL') {
                showSuccessToast(responseData?.message || 'Purchase order is not internal');
                setAtrItems([]);
                return; // Don't fetch items if there are none
            }
            
            // Then fetch the updated items
            await fetchATRItems();
        } catch (error: any) {
            console.error('ConfirmLinkJobCardModal: Error syncing ATR items:', error);
            const msg = error.response?.data?.message || error.message || '';
            if (msg.toLowerCase().includes('no assets found') || 
                msg.toLowerCase().includes('purchase order is not internal')) {
                showSuccessToast('No ATR items found for this order.');
                setAtrItems([]);
            } else if (msg.toLowerCase().includes('purchase order not found')) {
                if (msg.toLowerCase().includes('deleted as they were orphaned')) {
                    // Job card was deleted, show success message and close modal
                    showSuccessToast('Job card deleted as purchase order was not found. Closing modal...');
                    setTimeout(() => {
                        onClose();
                    }, 2000);
                    return; // Don't fetch items if there are none
                } else {
                    showErrorToast('Purchase order not found. Please verify the order number.');
                    setAtrItems([]);
                }
            } else {
                showErrorToast('Failed to sync ATR items: ' + msg);
            }
        } finally {
            setIsSyncingATR(false);
        }
    };

    const fetchATRItems = async () => {
        try {
            setIsLoadingATR(true);
            console.log('ConfirmLinkJobCardModal: Fetching ATR items for PO job card ID:', poJobCard.id);
            
            const response = await getATRItems(poJobCard.id);
            console.log('ConfirmLinkJobCardModal: ATR items response:', response);
            
            const items = response?.data?.data?.data || [];
            console.log('ConfirmLinkJobCardModal: Setting ATR items:', items.length, 'items');
            setAtrItems(items);
        } catch (error: any) {
            console.error('ConfirmLinkJobCardModal: Error fetching ATR items:', error);
            showErrorToast('Failed to fetch ATR items: ' + (error.response?.data?.message || error.message));
        } finally {
            setIsLoadingATR(false);
        }
    };

    const showATRCompletionModal = (item: any) => {
        setSelectedATRItem(item);
        setIsATRCompletionModalOpen(true);
    };

    const handleCompleteATRItem = async (itemId: number, createTask: boolean) => {
        try {
            await completeATRItem(itemId, createTask);
            // Update the local state
            setAtrItems(prev => prev.map(item => 
                item.id === itemId ? { ...item, completed: true } : item
            ));
            showSuccessToast('ATR item completed successfully');
        } catch (error: any) {
            console.error('Error completing ATR item:', error);
            showErrorToast('Failed to complete ATR item: ' + (error.response?.data?.message || error.message));
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    const formatTime = (timeString: string) => {
        return timeString ? timeString.substring(0, 5) : '';
    };

    return (
        <>
            <Dialog open={isOpen} onClose={onClose} className="relative z-50">
                <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-4xl max-h-[90vh] transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 text-left align-middle shadow-xl transition-all flex flex-col">
                        {/* Fixed Header */}
                        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <AlertTriangle className="text-amber-500" size={24} />
                                <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                                    Confirm Job Card Linking
                                </Dialog.Title>
                            </div>
                            <button
                                onClick={onClose}
                                disabled={isLoading}
                                className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 disabled:opacity-50"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Scrollable Body */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                                <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">
                                    What will happen:
                                </h4>
                                <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                                    <li>• The completed job card will receive the order number from the PO job card</li>
                                    <li>• All ATR items will be transferred to the completed job card</li>
                                    <li>• Any additional tasks from the PO job card will be copied over</li>
                                    <li>• The PO job card will be archived</li>
                                    <li>• You can undo this action within 7 days</li>
                                </ul>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* PO Job Card */}
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                    <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <FileText size={18} />
                                        PO Job Card (Source)
                                    </h3>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-900 dark:text-gray-100">#{poJobCard.job_number}</span>
                                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100 rounded">
                                                Order #{poJobCard.order_number}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                            <User size={16} />
                                            <span>{poJobCard.user?.name || 'Unknown'}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                            <MapPin size={16} />
                                            <span>{poJobCard.store?.name || 'Unknown Store'}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                            <Calendar size={16} />
                                            <span>{poJobCard.date ? formatDate(poJobCard.date) : 'No date'}</span>
                                        </div>
                                        {poJobCard.tasks && (
                                            <div className="text-gray-600 dark:text-gray-300">
                                                Tasks: {poJobCard.tasks.length}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Target Job Card */}
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                    <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <FileText size={18} />
                                        Completed Job Card (Target)
                                    </h3>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-900 dark:text-gray-100">#{targetJobCard.job_number}</span>
                                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100 rounded">
                                                Completed
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                            <User size={16} />
                                            <span>{targetJobCard.user?.name || 'Unknown'}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                            <MapPin size={16} />
                                            <span>{targetJobCard.store?.name || 'Unknown Store'}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                            <Calendar size={16} />
                                            <span>{targetJobCard.date ? formatDate(targetJobCard.date) : 'No date'}</span>
                                        </div>
                                        <div className="text-gray-600 dark:text-gray-300">
                                            Time: {formatTime(targetJobCard.time_in || '')} - {formatTime(targetJobCard.time_out || '')}
                                        </div>
                                        {targetJobCard.tasks && (
                                            <div className="text-gray-600 dark:text-gray-300">
                                                Tasks: {targetJobCard.tasks.length}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Result:</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                    Job Card <span className="font-medium text-gray-900 dark:text-gray-100">#{targetJobCard.job_number}</span> will be updated with Order <span className="font-medium text-gray-900 dark:text-gray-100">#{poJobCard.order_number}</span> 
                                    and will contain all work from both job cards. The PO job card will be archived.
                                </p>
                            </div>

                            <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <AlertTriangle className="text-red-600 dark:text-red-400 mt-0.5" size={20} />
                                    <div>
                                        <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">
                                            Confirm Job Card Linking
                                        </h4>
                                        <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                                            This action will merge the PO job card data into the completed job card. 
                                            The PO job card will be deleted after linking.
                                        </p>
                                        <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside space-y-1">
                                            <li>Order number will be added to the completed job card</li>
                                            <li>ATR items will be transferred</li>
                                            <li>Any additional tasks will be copied</li>
                                            <li>This action can be undone within 7 days</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* ATR Items Section */}
                            {poJobCard.order_number && (
                                <div>
                                    <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                                        <p className="text-sm text-blue-800 dark:text-blue-200">
                                            <strong>ATR Items from PO Job Card #{poJobCard.job_number}</strong> (Order #{poJobCard.order_number})
                                        </p>
                                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                                            These items will be transferred to the completed job card after linking.
                                        </p>
                                    </div>
                                    <ATRItemsBox
                                        atrItems={atrItems}
                                        isLoading={isLoadingATR}
                                        onSync={syncAndFetchATRItems}
                                        isSyncing={isSyncingATR}
                                        onComplete={showATRCompletionModal}
                                        showCompleteActions={true}
                                    />
                                    {atrItems.length > 0 && (
                                        <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
                                            <p className="text-sm text-green-800 dark:text-green-200">
                                                💡 <strong>Tip:</strong> Complete any pending ATR items above before linking the job cards.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Fixed Footer */}
                        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                            <button
                                onClick={onClose}
                                disabled={isLoading}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onConfirm}
                                disabled={isLoading}
                                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Linking...
                                    </>
                                ) : (
                                    'Confirm Link Job Cards'
                                )}
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>

            {/* ATR Completion Modal */}
            <ConfirmATRCompletionModal
                isOpen={isATRCompletionModalOpen}
                onClose={() => setIsATRCompletionModalOpen(false)}
                onConfirm={() => {
                    if (selectedATRItem) {
                        handleCompleteATRItem(selectedATRItem.id, true);
                    }
                    setIsATRCompletionModalOpen(false);
                }}
                assetName={selectedATRItem?.asset || ''}
            />
        </>
    );
} 